import React from 'react';
import {
  Box,
  Typography,
  useTheme,
  alpha,
  Skeleton,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { Clock, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

const GOLD = '#c9a96e';
const DATE_LOCALES = { fr, en: enUS };

const STATUS_COLORS = {
  DRAFT: '#78716c',
  IN_REVIEW: '#f59e0b',
  PENDING_SIGNATURE: '#3b82f6',
  SIGNED: '#22c55e',
};

function formatRelative(dateStr, lang = 'en') {
  if (!dateStr) return '';
  try {
    const d = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMin < 60) return `${diffMin}m`;
    if (diffHr < 24) return `${diffHr}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return format(d, 'dd MMM', { locale: DATE_LOCALES[lang] || enUS });
  } catch {
    return '';
  }
}

const RecentActivityFeed = ({ lifecycle }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const lang = i18n.language;

  if (lifecycle.loading) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Skeleton variant="text" width={160} height={24} sx={{ mb: 2 }} />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={40} sx={{ borderRadius: '8px', mb: 0.75 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  // Merge recent_work + action items, deduplicate by id
  const recentWork = lifecycle.data?.recent_work || [];
  const forApproval = lifecycle.data?.for_me_to_approve || [];
  const forSigning = lifecycle.data?.for_me_to_sign || [];

  const seen = new Set();
  const allItems = [];
  for (const item of [...forApproval, ...forSigning, ...recentWork]) {
    if (!seen.has(item.id)) {
      seen.add(item.id);
      allItems.push(item);
    }
  }

  const statusLabels = {
    DRAFT: t('clmDashboard.status.draft', 'Draft'),
    IN_REVIEW: t('clmDashboard.status.inReview', 'Review'),
    PENDING_SIGNATURE: t('clmDashboard.status.signing', 'Signing'),
    SIGNED: t('clmDashboard.status.signed', 'Signed'),
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '12px',
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 2px 8px rgba(0, 0, 0, 0.3)'
            : '0 2px 8px rgba(0, 0, 0, 0.04)',
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: GOLD,
            mb: 2,
            letterSpacing: '0.02em',
          }}
        >
          {t('clmDashboard.recentActivity', 'Recent Activity')}
        </Typography>

        {allItems.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, textAlign: 'center', py: 3 }}
          >
            {t('clmDashboard.noActivity', 'No recent activity')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {allItems.slice(0, 8).map((item) => {
              const statusColor = STATUS_COLORS[item.status] || '#78716c';
              return (
                <Box
                  key={item.id}
                  onClick={() => navigate(`/contract/${item.id}`)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    '&:hover': {
                      background: alpha(theme.palette.action.hover, 0.06),
                    },
                  }}
                >
                  {/* Icon */}
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '6px',
                      background: alpha(statusColor, 0.08),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <FileText size={14} color={statusColor} />
                  </Box>

                  {/* Title + type */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.title}
                    </Typography>
                    {item.type && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.68rem',
                        }}
                      >
                        {item.type}
                      </Typography>
                    )}
                  </Box>

                  {/* Status chip */}
                  <Chip
                    label={statusLabels[item.status] || item.status}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.62rem',
                      fontWeight: 600,
                      bgcolor: alpha(statusColor, 0.08),
                      color: statusColor,
                      border: `1px solid ${alpha(statusColor, 0.2)}`,
                    }}
                  />

                  {/* Time */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Clock size={11} color={theme.palette.text.secondary} />
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.68rem',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatRelative(item.updated_at, lang)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentActivityFeed;
